import { filterProductNames } from "../../../../Component/tool/productNameSearch";
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, StyleSheetManager } from 'styled-components';
import { createPortal } from 'react-dom';
import api from '../../../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../../../Component/server';
import { directUploadFiles, DirectUploadResult } from '../../../../Component/tool/directUpload';
import LiveDetailDialog from '../Component/LiveDetailDialog';
import { productRequestError } from '../productRequestError';
import { mediaUrl } from '../../../../Component/tool/mediaUrl';
import CaptureImageEditor, { cropCapturedImage, FULL_IMAGE_CROP } from './CaptureImageEditor';
import { LiveCaptureSource, visibleVideoFrame } from '../captureLiveImage';

type CaptureProduct = { seq: number; name: string; product_num?: string | number | null; product_status: string; thumbnail_img?: string | null };
export type SavedCapture = { productSeq: number; imgPath: string; sortOrder: number };
type Props = { liveSeq: number; captureImage: () => Promise<File>; getLiveVideo?: () => LiveCaptureSource | null; portalTarget?: HTMLElement | null; onClose: () => void; onSaved?: (saved: SavedCapture) => void; onCaptured?: (file: File) => void };

const productStatuses = ['', '판매노출', '판매', '대기'] as const;
const statusOrder: Record<string, number> = { '판매노출': 0, '판매': 1, '대기': 2 };
const captureImageSaveDirectUrl = `${SERVER_ADDRESS}product/capture-image-save-direct`;

export default function ProductCaptureModal({ liveSeq, captureImage, getLiveVideo, portalTarget, onClose, onSaved, onCaptured }: Props) {
  const [products, setProducts] = useState<CaptureProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [captures, setCaptures] = useState<File[]>([]);
  const [selectedCapture, setSelectedCapture] = useState(-1);
  const captureFiles = useRef<File[]>([]);
  const [preview, setPreview] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState('');
  const [cropReady, setCropReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const active = useRef(true);
  const currentLive = useRef(liveSeq);
  currentLive.current = liveSeq;
  const pendingThumbnails = useRef(new Map<number, { imgPath: string; previous: Set<string> }>());
  const captureRequest = useRef(0);
  const listRequest = useRef(0);
  const savingRef = useRef(false);
  const capturingRef = useRef(false);
  const cropRef = useRef(FULL_IMAGE_CROP);
  const uploadAbort = useRef<AbortController | null>(null);
  const pendingUpload = useRef<DirectUploadResult | null>(null);
  const idempotencyKey = useRef<string | null>(null);

  useEffect(() => {
    const view = portalTarget?.ownerDocument.defaultView;
    if (!view || view === window) return;
    view.addEventListener('pagehide', onClose);
    return () => view.removeEventListener('pagehide', onClose);
  }, [portalTarget, onClose]);

  const invalidatePendingSave = useCallback(() => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    pendingUpload.current = null;
    idempotencyKey.current = null;
  }, []);

  useEffect(() => {
    active.current = true;
    captureRequest.current++;
    listRequest.current++;
    capturingRef.current = false;
    savingRef.current = false;
    setSaving(false);
    setSaveSuccess(false);
    setSelected(null);
    setProducts([]);
    captureFiles.current = [];
    setCaptures([]);
    setSelectedCapture(-1);
    setFile(null);
    setCropReady(false);
    pendingThumbnails.current.clear();
    invalidatePendingSave();
    return () => {
      active.current = false;
      uploadAbort.current?.abort();
    };
  }, [liveSeq, invalidatePendingSave]);

  const loadProducts = useCallback(async () => {
    if (onCaptured) { setLoading(false); return; }
    const request = ++listRequest.current;
    setLoading(true);
    setListError('');
    try {
      const response = await api.get(url.Communication_url.capture_product_list, { params: { live_seq: liveSeq }, timeout: 10000 });
      if (!active.current || currentLive.current !== liveSeq || request !== listRequest.current) return;
      if (!Array.isArray(response.data) || response.data.some(product => !Number.isSafeInteger(product.seq) || typeof product.name !== 'string')) {
        throw new Error('상품 목록 응답을 확인할 수 없습니다. 다시 시도해주세요.');
      }
      const sortedProducts = response.data.map((product: CaptureProduct) => {
        const pending = pendingThumbnails.current.get(product.seq);
        if (!pending) return product;
        if (pending.previous.has(mediaUrl(product.thumbnail_img || ''))) return { ...product, thumbnail_img: pending.imgPath };
        pendingThumbnails.current.delete(product.seq);
        return product;
      }).sort((a: CaptureProduct, b: CaptureProduct) => (statusOrder[a.product_status] ?? 3) - (statusOrder[b.product_status] ?? 3));
      setProducts(sortedProducts);
      setSelected(current => current === null ? String(sortedProducts[0]?.seq ?? '') : current);
    } catch (error: any) {
      if (!active.current || currentLive.current !== liveSeq || request !== listRequest.current) return;
      if (error?.response?.status === 404 && error.response.data?.message === '정보없음') setProducts([]);
      else setListError(productRequestError(error, '상품 목록을 불러오지 못했습니다. 다시 시도해주세요.'));
    } finally {
      if (active.current && request === listRequest.current) setLoading(false);
    }
  }, [liveSeq, onCaptured]);

  const takeCapture = useCallback(async () => {
    if (savingRef.current || capturingRef.current) return;
    invalidatePendingSave();
    const request = ++captureRequest.current;
    capturingRef.current = true;
    setCapturing(true);
    setCaptureError('');
    setSaveSuccess(false);
    try {
      const image = await captureImage();
      if (active.current && request === captureRequest.current) {
        const next = [...captureFiles.current, image];
        captureFiles.current = next;
        setCaptures(next);
        setSelectedCapture(next.length - 1);
        setCropReady(false);
        cropRef.current = FULL_IMAGE_CROP;
        setFile(image);
      }
    } catch (error) {
      if (active.current && request === captureRequest.current) setCaptureError(productRequestError(error, '방송 화면을 캡처하지 못했습니다. 다시 시도해주세요.'));
    } finally {
      if (request === captureRequest.current) {
        capturingRef.current = false;
        if (active.current) setCapturing(false);
      }
    }
  }, [captureImage, invalidatePendingSave, liveSeq]);

  useEffect(() => { void loadProducts(); }, [loadProducts]);
  useEffect(() => { void takeCapture(); }, [takeCapture]);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const close = useCallback(() => { if (!savingRef.current) onClose(); }, [onClose]);
  const clearCaptures = () => {
    captureFiles.current = [];
    setCaptures([]);
    setSelectedCapture(-1);
    setFile(null);
    setCropReady(false);
    cropRef.current = FULL_IMAGE_CROP;
    setCaptureError('');
  };
  const selectCapture = (index: number) => {
    if (savingRef.current || capturingRef.current || index === selectedCapture) return;
    invalidatePendingSave();
    setSelectedCapture(index);
    setFile(captures[index]);
    setCropReady(false);
    cropRef.current = FULL_IMAGE_CROP;
    setCaptureError('');
    setSaveError('');
  };
  const save = async () => {
    if (savingRef.current || capturingRef.current || saveSuccess || !cropReady || !file || (!onCaptured && !products.some(product => product.seq === Number(selected)))) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    const captureVersion = captureRequest.current;
    const isCurrent = () => active.current && currentLive.current === liveSeq && captureRequest.current === captureVersion;
    try {
      if (onCaptured) {
        const croppedFile = await cropCapturedImage(file, cropRef.current);
        if (isCurrent()) {
          onCaptured(croppedFile);
          clearCaptures();
          setSaveSuccess(true);
        }
        return;
      }
      uploadAbort.current = new AbortController();
      if (!pendingUpload.current) {
        const croppedFile = await cropCapturedImage(file, cropRef.current);
        if (!isCurrent()) return;
        const uploaded = await directUploadFiles([croppedFile], {
          purpose: 'product-image',
          liveSeq,
          signal: uploadAbort.current.signal,
        });
        if (!isCurrent()) return;
        pendingUpload.current = uploaded;
      }
      const uploaded = pendingUpload.current;
      if (uploaded.objects.length !== 1 || !uploaded.objects[0]?.objectId) {
        throw new Error('업로드된 캡처 이미지를 확인할 수 없습니다.');
      }
      idempotencyKey.current ??= crypto.randomUUID();
      const response = await api.post(captureImageSaveDirectUrl, {
        productSeq: Number(selected),
        uploadSessionId: uploaded.sessionId,
        imageObjectId: uploaded.objects[0].objectId,
        idempotencyKey: idempotencyKey.current,
      }, { headers: { 'Content-Type': 'application/json' }, signal: uploadAbort.current.signal });
      if (!isCurrent()) return;
      if (!Number.isSafeInteger(response.data?.seq)
        || Number(response.data.productSeq) !== Number(selected)
        || typeof response.data.imgPath !== 'string'
        || !response.data.imgPath.trim()
        || !Number.isSafeInteger(response.data.sortOrder)
        || response.data.sortOrder < 0) {
        throw new Error('이미지 저장 결과를 확인할 수 없습니다. 상품 목록을 확인해주세요.');
      }
      pendingUpload.current = null;
      idempotencyKey.current = null;
      const saved: SavedCapture = { productSeq: Number(response.data.productSeq), imgPath: response.data.imgPath, sortOrder: response.data.sortOrder };
      if (saved.sortOrder === 0) {
        const previous = new Set(pendingThumbnails.current.get(saved.productSeq)?.previous);
        previous.add(mediaUrl(products.find(product => product.seq === saved.productSeq)?.thumbnail_img || ''));
        previous.delete(mediaUrl(saved.imgPath));
        pendingThumbnails.current.set(saved.productSeq, { imgPath: saved.imgPath, previous });
        setProducts(current => current.map(product => product.seq === saved.productSeq ? { ...product, thumbnail_img: saved.imgPath } : product));
      }
      setSaveSuccess(true);
      clearCaptures();
      void loadProducts();
      onSaved?.(saved);
    } catch (error) {
      if (isCurrent()) setSaveError(productRequestError(error, '캡처 이미지를 저장하지 못했습니다. 다시 시도해주세요.'));
    } finally {
      if (isCurrent()) {
        uploadAbort.current = null;
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  const displayedProducts = filterProductNames(products.filter(product => !status || product.product_status === status), searchInput);

  const resetSelection = () => {
    invalidatePendingSave();
    setSelected('');
  };
  const changeCrop = useCallback((nextCrop: typeof FULL_IMAGE_CROP) => {
    const current = cropRef.current;
    if (current.x === nextCrop.x && current.y === nextCrop.y && current.width === nextCrop.width && current.height === nextCrop.height) return;
    invalidatePendingSave();
    cropRef.current = nextCrop;
  }, [invalidatePendingSave]);
  const selectProduct = (productSeq: number) => {
    invalidatePendingSave();
    setSelected(String(productSeq));
  };
  const refreshProducts = () => {
    resetSelection();
    void loadProducts();
  };

  const standalone = !!portalTarget && portalTarget.ownerDocument !== document;
  const content = <>
    <CaptureDialogSkin $standalone={standalone} />
    <LiveDetailDialog open size="large" title="이미지 등록" onClose={close} closeOnlyByButton>
      <Content $standalone={standalone} data-product-capture-modal="true">
        <Workspace $standalone={standalone}>
          <CapturePanel>
            <CaptureStage>
              {preview
                ? <CaptureImageEditor key={selectedCapture} src={preview} fitContainer disabled={saving || capturing} onReadyChange={setCropReady} onCropChange={changeCrop} />
                : <Preview><p role="status">{capturing ? '방송 화면을 캡처하고 있습니다.' : '캡처한 사진이 없습니다.'}</p></Preview>}
              {getLiveVideo && <LiveCapturePreview getVideo={getLiveVideo} />}
            </CaptureStage>
            {captureError && <ErrorMessage role="alert">{captureError}</ErrorMessage>}
          </CapturePanel>

          {!onCaptured && <ProductPanel>
            <Filters role="group" aria-label="상품 상태 필터">
              {productStatuses.map(value => <button key={value} type="button" aria-pressed={status === value} disabled={saving} onClick={() => { setStatus(value); resetSelection(); }}>{value || '전체'}</button>)}
            </Filters>

            <Search data-admin-search="" onSubmit={event => event.preventDefault()} role="search">
              <input
                type="search"
                aria-label="캡처 등록 상품 검색"
                placeholder="상품명을 검색해주세요."
                value={searchInput}
                disabled={saving}
                onChange={event => { setSearchInput(event.target.value); resetSelection(); }}
              />
              <SearchButton type="submit" disabled={saving}>검색</SearchButton>
              <RefreshButton data-admin-search-reset="" type="button" aria-label="상품 목록 새로고침" disabled={saving || loading} onClick={refreshProducts}>
                <RefreshIcon aria-hidden="true" viewBox="0 0 24 24"><path d="M20 11a8 8 0 1 0-2.34 5.66"/><path d="M20 5v6h-6"/></RefreshIcon>
              </RefreshButton>
            </Search>

            <ProductResults>
              {products.length > 0 && listError && <ResultState role="alert"><strong>{listError}</strong><button type="button" onClick={loadProducts}>목록 다시 시도</button></ResultState>}
              {loading && products.length === 0
                ? <ResultState role="status"><strong>상품 목록을 불러오고 있습니다.</strong><span>잠시만 기다려주세요.</span></ResultState>
                : listError && products.length === 0
                  ? <ResultState role="alert"><strong>{listError}</strong><button type="button" onClick={loadProducts}>목록 다시 시도</button></ResultState>
                  : products.length === 0
                    ? <ResultState role="status"><strong>등록된 상품이 없습니다.</strong><span>먼저 라이브 상품을 등록해주세요.</span></ResultState>
                    : displayedProducts.length === 0
                      ? <ResultState role="status"><strong>검색 결과가 없습니다.</strong><span>상품명이나 상태 필터를 변경해주세요.</span></ResultState>
                      : <ProductList role="radiogroup" aria-label="이미지를 등록할 상품">
                        {displayedProducts.map(product => <ProductRow key={product.seq}>
                          <input
                            type="radio"
                            name="capture-product"
                            value={product.seq}
                            aria-label={`${product.product_num != null ? `No.${product.product_num} ` : ''}${product.name} ${product.product_status}`}
                            checked={selected === String(product.seq)}
                            disabled={saving}
                            onChange={() => selectProduct(product.seq)}
                          />
                          {product.thumbnail_img
                            ? <ProductThumbnail src={mediaUrl(product.thumbnail_img)} alt="" />
                            : <ThumbnailPlaceholder aria-label="상품 이미지 없음" />}
                          <ProductIdentity>
                            {product.product_num != null && <small>No.{product.product_num}</small>}
                            <span>{product.name}</span>
                          </ProductIdentity>
                          <Status $status={product.product_status}>{product.product_status}</Status>
                        </ProductRow>)}
                      </ProductList>}
            </ProductResults>
          </ProductPanel>}
        </Workspace>

        {saveError && <SaveError role="alert">{saveError}</SaveError>}
        {saveSuccess && <SuccessMessage role="status">이미지를 등록했습니다. 다른 이미지를 등록하려면 다시 캡처해 주세요.</SuccessMessage>}
        {captures.length > 0 && <CaptureHistory role="group" aria-label="캡처 기록">
          {captures.map((capture, index) => <button key={index} type="button" aria-label={`${index + 1}회차 캡처`} aria-pressed={selectedCapture === index}
            disabled={capturing || saving} onClick={() => selectCapture(index)}>
            <CaptureHistoryThumbnail file={capture} index={index} />
            <span>{index + 1}회차 캡처</span>
          </button>)}
        </CaptureHistory>}
        <Actions>
          <RecaptureButton type="button" onClick={takeCapture} disabled={capturing || saving}>{capturing ? '캡처 중...' : '다시 캡처'}</RecaptureButton>
          <SaveButton type="button" onClick={save} disabled={saving || capturing || loading || !!listError || saveSuccess || !cropReady || !file || (!onCaptured && !selected)}>{saving ? '이미지 등록 중...' : saveSuccess ? '이미지 등록 완료' : onCaptured ? '상품 사진에 추가' : '이미지 등록하기'}</SaveButton>
        </Actions>
      </Content>
    </LiveDetailDialog>
  </>;
  return portalTarget ? createPortal(<StyleSheetManager target={portalTarget.ownerDocument.head}>{content}</StyleSheetManager>, portalTarget) : content;
}

function CaptureHistoryThumbnail({ file, index }: { file: File; index: number }) {
  const [source, setSource] = useState('');
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSource(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return <img src={source || undefined} alt={`${index + 1}회차 캡처 썸네일`} />;
}

const CaptureHistory = styled.div`
  display: flex;
  flex: none;
  overflow-x: auto;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4) var(--space-1);
  && button {
    display: flex;
    flex: none;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    width: calc(var(--space-8) * 4);
    padding: var(--space-1);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    background: var(--color-bg-surface);
    color: var(--color-text-secondary);
    font-family: var(--font-family-base);
    font-size: var(--text-caption);
    font-weight: 500;
  }
  img {
    display: block;
    width: 100%;
    height: calc(var(--space-8) * 2);
    object-fit: contain;
    border-radius: var(--radius-sm);
    background: var(--color-bg-muted);
  }
  button:hover:not(:disabled) { border-color: var(--color-brand); }
  && button:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  && button:disabled {
    opacity: 1;
    background: var(--color-bg-muted);
    color: var(--color-text-disabled);
  }
  button[aria-pressed='true'] {
    border-color: var(--color-brand);
    background: var(--color-brand-subtle);
    color: var(--color-brand);
  }
`;

function LiveCapturePreview({ getVideo }: { getVideo: () => LiveCaptureSource | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const view = canvasRef.current?.ownerDocument.defaultView ?? window;
    let frame = 0;
    let lastDraw = -100;
    let previousReady = false;
    const draw = (time: number) => {
      frame = view.requestAnimationFrame(draw);
      if (time - lastDraw < 100) return;
      lastDraw = time;
      const video = getVideo();
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');
      let available = false;
      if (video && canvas && context && ('videoWidth' in video
        ? video.readyState >= 2 && video.videoWidth && video.videoHeight && !video.ended && !video.paused
        : video.width > 0 && video.height > 0)) {
        try {
          const visible = visibleVideoFrame(video);
          canvas.width = 160;
          canvas.height = Math.round(160 / visible.displayRatio);
          context.drawImage(video, visible.x, visible.y, visible.width, visible.height, 0, 0, canvas.width, canvas.height);
          available = true;
        } catch { /* The next decoded frame can recover a temporarily unavailable preview. */ }
      }
      if (available !== previousReady) { previousReady = available; setReady(available); }
    };
    frame = view.requestAnimationFrame(draw);
    return () => view.cancelAnimationFrame(frame);
  }, [getVideo]);

  return <MiniLive aria-label="실시간 라이브 미리보기"><span>{ready ? '실시간 라이브' : '방송 대기'}</span><canvas ref={canvasRef} style={{ display: ready ? 'block' : 'none' }} /></MiniLive>;
}

const CaptureDialogSkin = createGlobalStyle<{ $standalone: boolean }>`
  div[role='dialog']:has([data-product-capture-modal='true']) {
    width: min(1120px, 100%);
    border-radius: 10px;
  }

  div[role='dialog']:has([data-product-capture-modal='true']) > div:first-child {
    position: relative;
    justify-content: center;
    padding: 18px 64px;
  }

  div[role='dialog']:has([data-product-capture-modal='true']) > div:first-child > div:first-child { text-align: center; }
  div[role='dialog']:has([data-product-capture-modal='true']) > div:first-child h2 { font-size: 18px; }

  div[role='dialog']:has([data-product-capture-modal='true']) > div:first-child > button {
    position: absolute;
    top: 10px;
    right: 16px;
    margin: 0;
  }

  div[role='dialog']:has([data-product-capture-modal='true']) > div:last-child {
    overflow: hidden;
    padding: 0;
  }

  ${({ $standalone }) => $standalone && `
    div:has(> div[role='dialog'] [data-product-capture-modal='true']) {
      padding: 0;
      background: var(--color-bg-surface);
      backdrop-filter: none;
    }

    div[role='dialog']:has([data-product-capture-modal='true']) {
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
      box-shadow: none;
    }

    div[role='dialog']:has([data-product-capture-modal='true']) > div:first-child { flex-shrink: 0; }
    div[role='dialog']:has([data-product-capture-modal='true']) > div:last-child { display: flex; }
  `}
`;

const Content = styled.div<{ $standalone: boolean }>`
  display: flex;
  min-height: 0;
  max-height: calc(100dvh - var(--space-8) * 4);
  flex-direction: column;
  color: var(--admin-theme-text, #29262f);

  * { box-sizing: border-box; }
  button, input { font: inherit; }
  button { cursor: pointer; }
  button:disabled { cursor: not-allowed; opacity: 0.5; }
  button:focus-visible, input:focus-visible { outline: 3px solid #9576ee; outline-offset: 2px; }

  @media (max-width: 760px) { height: calc(100dvh - 86px); }

  ${({ $standalone }) => $standalone && `
    flex: 1;
    height: 100%;
    max-height: none;
    > :not(:first-child) { flex-shrink: 0; }
  `}
`;

const Workspace = styled.div<{ $standalone: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 0.96fr) minmax(0, 1.04fr);
  min-height: 0;
  height: ${({ $standalone }) => $standalone ? 'auto' : 'min(67vh, 610px)'};
  ${({ $standalone }) => $standalone && 'flex: 1;'}

  @media (max-width: 1024px) { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); }

  @media (max-width: 760px) {
    display: flex;
    height: auto;
    max-height: none;
    flex: 1;
    flex-direction: column;
    overflow-y: auto;
  }
`;

const CapturePanel = styled.section`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  padding: 14px;
  background: #1d1a22;
  &:only-child { grid-column: 1 / -1; }

  @media (max-width: 760px) { min-height: 420px; }
`;

const CaptureStage = styled.div`
  position: relative;
  display: grid;
  min-height: 0;
  flex: 1;
  place-items: center;
  overflow: hidden;
  background: #18161c;

  > div:first-child > div + div, > div:first-child > details { display: none; }
`;

const Preview = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 280px;
  place-items: center;
  background: #18161c;

  p { max-width: 25ch; margin: 0; color: var(--admin-theme-muted, #cdc8d4); line-height: 1.6; text-align: center; }
`;

const MiniLive = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 96px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 4px;
  background: #241c30;
  color: #fff;
  pointer-events: none;

  span { display: block; padding: 6px 4px; font-size: 11px; text-align: center; }
  canvas { display: block; width: 100%; height: auto; }
`;

const ProductPanel = styled.section`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px 0;
  background: var(--admin-theme-surface, #fff);

  @media (max-width: 760px) { min-height: 420px; padding-bottom: 18px; }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    min-height: 36px;
    padding: 7px 16px;
    border: 1px solid var(--admin-theme-border, #e3e0e7);
    border-radius: 999px;
    background: var(--admin-theme-surface, #fff);
    color: var(--admin-theme-muted, #918b98);
    font-size: 13px;
  }

  button:hover:not(:disabled) { border-color: #39363d; color: var(--admin-theme-text, #39363d); }
  button[aria-pressed='true'] { border-color: #2e2b31; background: #2e2b31; color: #fff; }
`;

const Search = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 10px;

  input {
    width: 100%;
    min-width: 0;
    min-height: 48px;
    padding: 12px 14px;
    border: 1px solid var(--admin-theme-border, #e6e3e9);
    border-radius: 8px;
    color: var(--admin-theme-text, #29262f);
    font-size: 15px;
  }

  input::placeholder { color: var(--admin-theme-muted, #918b98); }
`;

const SearchButton = styled.button`
  min-width: 76px;
  min-height: 48px;
  padding: 10px 18px;
  border: 1px solid #2f2d31;
  border-radius: 8px;
  background: #2f2d31;
  color: #fff;
  font-weight: 600;
`;

const RefreshButton = styled.button`
  display: inline-grid;
  width: 48px;
  height: 48px;
  padding: 0;
  place-items: center;
  border: 1px solid var(--admin-theme-border, #e6e3e9);
  border-radius: 8px;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-theme-text, #171519);

  &:hover:not(:disabled) { background: var(--admin-theme-raised, #f6f4f8); }
`;

const RefreshIcon = styled.svg`
  width: 25px;
  height: 25px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
`;

const ProductResults = styled.div`
  min-height: 0;
  flex: 1;
  border-top: 1px solid var(--admin-theme-border, #f0edf2);
`;

const ProductList = styled.div`
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: var(--admin-theme-muted, #c9c4ce) transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar { width: 7px; }
  &::-webkit-scrollbar-thumb { border-radius: 999px; background: var(--admin-theme-accent-soft, #c9c4ce); }
`;

const ProductRow = styled.label`
  display: grid;
  grid-template-columns: 20px 52px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  min-height: 70px;
  padding: 9px 4px;
  border-bottom: 1px solid var(--admin-theme-border, #f0edf2);
  cursor: pointer;

  &:hover { background: var(--admin-theme-raised, #faf9fb); }
  &:has(input:checked) { background: var(--admin-theme-accent-soft, #f5f1ff); }
  input { width: 18px; height: 18px; margin: 0; accent-color: var(--admin-theme-link, #6035d4); }
`;

const ProductThumbnail = styled.img`
  width: 52px;
  height: 52px;
  border: 1px solid var(--admin-theme-border, #ece9ef);
  border-radius: 4px;
  object-fit: contain;
  background: var(--admin-theme-raised, #faf9fb);
`;

const ThumbnailPlaceholder = styled.span`
  width: 52px;
  height: 52px;
  border: 1px solid var(--admin-theme-border, #ece9ef);
  border-radius: 4px;
  background: var(--admin-theme-raised, #faf9fb);
`;

const ProductIdentity = styled.span`
  min-width: 0;
  line-height: 1.45;

  small { display: block; color: var(--admin-theme-muted, #8a8490); font-size: 11px; }
  span { display: block; overflow-wrap: anywhere; color: var(--admin-theme-text, #36323a); font-size: 14px; }
`;

const Status = styled.small<{ $status: string }>`
  min-width: 48px;
  padding: 6px 8px;
  border-radius: 4px;
  background: ${props => props.$status === '판매노출' ? 'var(--admin-theme-danger-soft, #ffe7ea)' : props.$status === '판매' ? 'var(--admin-theme-success-soft, #e6f8ef)' : 'var(--admin-theme-raised, #f0f0f2)'};
  color: ${props => props.$status === '판매노출' ? 'var(--admin-theme-danger, #f03d5c)' : props.$status === '판매' ? 'var(--admin-theme-success, #11985e)' : 'var(--admin-theme-muted, #9b969f)'};
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

const ResultState = styled.div`
  display: grid;
  min-height: 180px;
  padding: 28px;
  place-content: center;
  gap: 8px;
  color: var(--admin-theme-muted, #7d7782);
  text-align: center;

  strong { color: var(--admin-theme-text, #3d3941); font-size: 15px; }
  span { font-size: 13px; }
  button { min-height: 40px; margin: 8px auto 0; padding: 8px 14px; border: 1px solid var(--admin-theme-border, #dcd7e2); border-radius: 8px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-link, #6035d4); }
`;

const ErrorMessage = styled.p`
  margin: 12px 0 0;
  color: var(--admin-theme-danger, #d92f49);
  font-size: 13px;
  line-height: 1.5;
`;

const SaveError = styled(ErrorMessage)`
  margin: 0;
  padding: 10px 20px;
  border-top: 1px solid var(--admin-theme-border, #f2d9de);
  background: var(--admin-theme-danger-soft, #fff7f8);
`;

const SuccessMessage = styled.p`
  margin: 0;
  padding: 10px 20px;
  border-top: 1px solid var(--admin-theme-border, #d8ebe0);
  background: var(--admin-theme-raised, #f3faf6);
  color: var(--admin-theme-success, #176b43);
  font-size: 13px;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: minmax(128px, 0.28fr) minmax(0, 1fr);
  gap: 8px;
  padding: 8px;
  border-top: 1px solid var(--admin-theme-border, #ece8f0);
  background: var(--admin-theme-surface, #fff);

  button { min-height: 48px; border-radius: 6px; font-weight: 600; }

  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const RecaptureButton = styled.button`
  border: 1px solid #7046df;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-theme-link, #5b31ca);

  &:hover:not(:disabled) { background: var(--admin-theme-accent-soft, #f6f2ff); }
`;

const SaveButton = styled.button`
  border: 1px solid #6136d7;
  background: #6136d7;
  color: #fff;

  &:hover:not(:disabled) { background: #5129c3; }
`;
